import { useState, useCallback } from 'react'
import AccessAPI from '../api/access_web_api'
import Status from '../constants/request_statuses'
import { serializableError } from '@utils/serializable_error'
import { createLogger } from '@utils/logger'

const log = createLogger('DynamicAuthorization')

export interface DynamicAbilityType {
  ability: string
  permissionable_id: number
  permissionable_type: string
  result: boolean
}

interface UseDynamicAuthorizationParams {
  resourceId: string | number
  resourceType: string
  abilities: string[]
  apiService?: typeof AccessAPI
}

interface UseDynamicAuthorizationResult {
  dynamicAbilities: any[]
  status: string
  error: any | null
  refreshAbilities: () => void
  can: (ability: string) => boolean
}

// This hook provides a way to check dynamic abilities for a given resource
function useDynamicAuthorization({
  resourceId,
  resourceType,
  abilities,
  apiService = AccessAPI,
}: UseDynamicAuthorizationParams): UseDynamicAuthorizationResult {
  const [dynamicAbilities, setDynamicAbilities] = useState<DynamicAbilityType[]>([])
  const [status, setStatus] = useState<string>(Status.IDLE)
  const [error, setError] = useState<any | null>(null)

  const refreshAbilities = useCallback(async () => {
    setStatus(Status.LOADING)
    try {
      const response = await apiService.checkAbilities(abilities, resourceType, resourceId)
      const parsedJSON = response.data
      if (response.status === 200) {
        setDynamicAbilities(parsedJSON.data as DynamicAbilityType[])
        setStatus(Status.SUCCEEDED)
      } else {
        // Do nothing
      }
    } catch (err) {
      setError(serializableError(err))
      setStatus(Status.FAILED)
    }
  }, [abilities, resourceType, resourceId, apiService])

  const can = (ability: string) => {
    const matchingAbilities = dynamicAbilities.filter(item => item.ability === ability)

    if (matchingAbilities.length === 0) {
      log.warn(`Warning: No ability found for '${ability}'. Check for spelling mistakes.`)
      return false
    }

    return matchingAbilities.some(item => item.result === true)
  }

  return {
    dynamicAbilities,
    status,
    error,
    refreshAbilities,
    can,
  }
}

export default useDynamicAuthorization
